/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { captureReferralCode } from "@shared/utils/referral-storage";
import { getRealLocalStorage } from "@shared/utils/storage/localStorageWrapper";

/**
 * Normalizes pathname so `/early-access/` and `/early-access` are treated the same.
 */
export function isEarlyAccessLandingPathname(pathname: string): boolean {
  return (pathname || "").replace(/\/+$/, "") === "/early-access";
}

/**
 * Reports whether a stored return URL points at a protected `/app/*` route.
 * Used to decide whether a freshly-requested protected deep link may supersede
 * a previously-saved one without clobbering a non-protected landing (e.g. an
 * early-access referral URL).
 *
 * @param savedReturnPath - The absolute return URL currently in storage.
 * @returns True when the saved URL's pathname is under `/app`.
 */
export function isProtectedAppReturnPath(savedReturnPath: string | null): boolean {
  try {
    return Boolean(savedReturnPath) && new URL(savedReturnPath as string).pathname.startsWith("/app");
  } catch {
    return false;
  }
}

/**
 * Decides whether the current protected deep link should overwrite the stored
 * return path. A fresh value is written when storage is empty, or when both the
 * current location and the stored value are protected `/app/*` routes — the
 * freshly-requested deep link then wins over a stale protected one (ADO #3863)
 * while a non-protected landing (e.g. an early-access referral URL) is kept.
 *
 * @param existing - The return URL currently in storage, if any.
 * @returns True when the current location should replace the stored value.
 */
function shouldWriteCurrentReturnPath(existing: string | null): boolean {
  if (!existing) return true;
  const currentIsProtectedApp = (window.location.pathname || "").startsWith("/app");
  return currentIsProtectedApp && isProtectedAppReturnPath(existing);
}

/**
 * Persists referral and post-login return URL on the first paint of a protected route.
 * Must run even when `withAuthenticationRequired` does not mount the inner page yet
 * (unauthenticated first visit → OIDC redirect).
 *
 * A freshly-requested protected `/app/*` deep link always wins over an existing
 * value that itself targets `/app/*` (ADO #3863: a stale protected returnPath —
 * left by an aborted login or an intra-app navigation helper — otherwise
 * silently replaced the link the user actually clicked, stranding them on a
 * default page after login in non-prod). A saved non-protected landing (e.g. an
 * early-access referral URL) is still preserved.
 */
function persistReturnUrl(realStorage: Storage): void {
  if (isEarlyAccessLandingPathname(window.location.pathname)) {
    realStorage.setItem("returnPath", window.location.href);
    return;
  }
  if (shouldWriteCurrentReturnPath(realStorage.getItem("returnPath"))) {
    realStorage.setItem("returnPath", window.location.href);
  }
}

/**
 * Captures the referral code and persists the post-login return URL when a
 * protected landing route is first painted. Safe to call on the server (no-op)
 * and when storage is unavailable (swallows the error).
 */
export function persistProtectedLandingReturnPath(): void {
  if (typeof window === "undefined") return;
  try {
    captureReferralCode(window.location.search || "");
    persistReturnUrl(getRealLocalStorage());
  } catch {
    /* storage unavailable */
  }
}
